import { modTypes } from "@blings/blings-player";
import { updateObjectValues } from "../../../helpers/JsonSearch";
import type { IModModel } from "../../../stores/ModsStore";
import type { TimelineLayerInfo } from "./layerHierarchy";
import { normalizeKeyframePath } from "./keyframeHelpers";
import { getLayerData } from "./layerHierarchy";
import { isReadableMod, modTargetsLayer } from "./timingModHelpers";

type ResolvedCustomMod = {
  index: number;
  path: string;
  value: unknown;
};

function cloneLayerData<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function cloneModValue<T>(value: T): T {
  if (value === null || typeof value !== "object") {
    return value;
  }

  return cloneLayerData(value);
}

function normalizeObjectPath(path: string): string {
  return normalizeKeyframePath(path);
}

export function applyCustomModsToLayerData(
  layerData: unknown,
  layer: TimelineLayerInfo,
  mods: IModModel[]
): unknown {
  if (!layerData) {
    return null;
  }

  const relevantMods = mods
    .flatMap((mod, index): ResolvedCustomMod[] => {
      if (!isReadableMod(mod)) return [];

      const { moddata } = mod;
      if (moddata.type !== modTypes.custom) return [];
      if (moddata.isDisabled) return [];
      if (typeof moddata.path !== "string") return [];
      if (!modTargetsLayer(mod, layer)) return [];

      return [
        {
          index,
          path: moddata.path,
          value: cloneModValue(moddata.value),
        },
      ];
    })
    .sort((left, right) => {
      const leftDepth = normalizeKeyframePath(left.path).split(".").length;
      const rightDepth = normalizeKeyframePath(right.path).split(".").length;

      if (leftDepth !== rightDepth) {
        return leftDepth - rightDepth;
      }

      return right.index - left.index;
    });

  if (!relevantMods.length) {
    return layerData;
  }

  const resolvedLayerData = cloneLayerData(layerData);

  for (const mod of relevantMods) {
    updateObjectValues(
      resolvedLayerData as Record<string, unknown>,
      [normalizeKeyframePath(mod.path)],
      [mod.value]
    );
  }

  return resolvedLayerData;
}

export function cloneAnimationWithAppliedLayerMods(
  animation: unknown,
  layer: TimelineLayerInfo,
  mods: IModModel[]
): unknown {
  if (!animation) {
    return null;
  }

  const clonedAnimation = cloneLayerData(animation);
  const clonedLayerData = getLayerData(clonedAnimation, layer.layerPath);
  const resolvedLayerData = applyCustomModsToLayerData(
    clonedLayerData,
    layer,
    mods
  );

  if (!resolvedLayerData) {
    return clonedAnimation;
  }

  updateObjectValues(
    clonedAnimation as Record<string, unknown>,
    [normalizeObjectPath(layer.layerPath)],
    [resolvedLayerData]
  );

  return clonedAnimation;
}
